import { useEffect, useState } from "react";
import { Mail, Loader2, CheckCircle2, RefreshCw, Info } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Gmail loyalty balance auto-detect card (spec §deferred).
   Reuses backend/gmail_sync.py flow with a loyalty-specific /scan-loyalty endpoint. */
export default function GmailLoyaltyCard() {
  const [status, setStatus] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/gmail/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  }, []);

  if (!status) return null;
  if (!status.configured) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 flex items-start gap-3" data-testid="gmail-loyalty-unconfigured">
        <Info className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700">
          <div className="font-800 text-slate-900">Auto-detect balances from Gmail</div>
          <p className="text-xs mt-1 text-slate-600">
            Once your admin adds <code className="bg-slate-200 px-1 rounded">GOOGLE_CLIENT_ID</code> +{" "}
            <code className="bg-slate-200 px-1 rounded">GOOGLE_CLIENT_SECRET</code> to backend/.env, this card lights up so
            you can connect Gmail read-only and pull the latest miles balance from monthly airline statements.
          </p>
        </div>
      </div>
    );
  }

  const connect = async () => {
    try {
      const r = await api.get("/gmail/connect");
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Gmail connect");
    }
  };

  const scan = async () => {
    setScanning(true);
    setResult(null);
    try {
      const r = await api.post("/gmail/scan-loyalty");
      setResult(r.data);
      if (r.data.updated > 0) {
        toast.success(`Updated ${r.data.updated} balance${r.data.updated === 1 ? "" : "s"}`);
      } else {
        toast.info("No new balance emails detected in the last 60 days");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally { setScanning(false); }
  };

  const disconnect = async () => {
    try {
      await api.delete("/gmail/disconnect");
      setStatus({ ...status, connected: false });
      toast.success("Gmail disconnected");
    } catch { toast.error("Could not disconnect"); }
  };

  return (
    <div className="rounded-2xl border border-blue-300 bg-blue-50 p-5" data-testid="gmail-loyalty-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] font-800 uppercase tracking-widest text-blue-700 flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" /> Loyalty · Auto-Detect
          </div>
          <h3 className="font-serif font-800 text-xl text-slate-900 mt-1">Pull balances from monthly airline statements</h3>
          <p className="text-sm text-slate-600 mt-1 max-w-md">
            Read-only Gmail connection scans the last 60 days for United, Delta, American, Alaska, Southwest, JetBlue,
            Hawaiian and Frontier statement emails, then updates your loyalty records automatically.
          </p>
          {status.connected && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-800">
              <CheckCircle2 className="w-4 h-4" /> Connected as <b className="ml-0.5">{status.email}</b>
              {status.last_loyalty_scan && <> · last scan {status.last_loyalty_scan.slice(0, 16).replace("T", " ")}</>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!status.connected ? (
            <button onClick={connect} data-testid="gmail-connect-btn"
              className="inline-flex items-center gap-1.5 bg-blue-600 text-white text-sm font-800 px-4 py-2.5 rounded-lg hover:bg-blue-700">
              <Mail className="w-4 h-4" /> Connect Gmail
            </button>
          ) : (
            <>
              <button onClick={scan} disabled={scanning} data-testid="gmail-scan-loyalty-btn"
                className="inline-flex items-center gap-1.5 bg-blue-600 text-white text-sm font-800 px-4 py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {scanning ? "Scanning…" : "Scan now"}
              </button>
              <button onClick={disconnect} data-testid="gmail-disconnect-btn"
                className="text-xs text-slate-600 underline hover:text-slate-900">Disconnect</button>
            </>
          )}
        </div>
      </div>

      {result && result.records && result.records.length > 0 && (
        <div className="mt-4 border-t border-blue-200 pt-3" data-testid="gmail-scan-result">
          <div className="text-[10px] font-800 uppercase tracking-widest text-blue-700 mb-2">Latest balances found</div>
          <div className="flex flex-wrap gap-2">
            {result.records.map((r, i) => (
              <span key={i} className="text-xs font-800 bg-white text-slate-900 border border-slate-200 px-2.5 py-1 rounded-full">
                {r.program}: <b className="text-blue-700">{r.balance.toLocaleString()}</b> {r.currency}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
